/**
 * Registros de Produtos (lista) — conteúdo renderizado dentro do shell de Databases.
 *
 * O shell (DatabasesPage) já provê o header global (breadcrumb + seletor de base +
 * Configurações); aqui vive apenas a barra de ações do conteúdo: busca centralizada,
 * refresh, view-toggle (Cards/Lista) e o primário Cadastrar — seguindo o padrão-lista
 * do Design System (ref. UsersPage). Os filtros avançados (chip-based) continuam no
 * ProductsFilterChipsBar. Duas views: cards (grid) e tabela (que vira cards no mobile).
 */

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import type {
  DatabaseProductData,
  DatabaseDocumentResponse,
  DatabaseDocumentTransferRejectionCode,
  DatabaseDocumentTransferResult,
  BusinessOpportunity,
  ProductVariation,
} from '@troia-v3/types';
// Import de VALOR (não `import type`): é a MESMA regra que o backend usa para
// recusar o "mover" — uma regra, um lugar. `DatabaseType` é enum real (não
// union de string literal) — o modal exige o membro do enum, não a string crua.
import { DatabaseType, isDocumentIntegrationLinked } from '@troia-v3/types';
import { TransferDocumentsDialog } from '../TransferDocumentsDialog';
import { useCopyDatabaseDocuments, useDatabaseDocuments, useDeleteDatabaseDocument } from '../../hooks/useDatabaseDocuments';
import {
  Button,
  ListPageLoading,
  ListPageEmpty,
  ListPagePagination,
  PageToolbar,
  PlainHeadCell,
  SelectAllHeadCell,
  SelectRowCell,
  SortableHeadCell,
  StatusPill,
  ToolbarSearch,
  ViewToggle,
  type StatusPillVariant,
} from '@/shared/components/ui';
import { CardActionsMenu, type CardAction } from '@/shared/components/ui/CardActionsMenu';
import { useConfirm } from '@/shared/components/ui/ConfirmProvider';
import { useIsMobile, usePageSelection } from '@/shared/hooks';
import { ProductsFilterChipsBar } from './ProductsFilterChipsBar';
import { ProductCard, formatPrice } from './ProductCard';
import type { ProductsFilters } from './ProductsAdvancedFilters';
import { Icon } from '@/shared/icons';

type ProductDocument = DatabaseDocumentResponse<DatabaseProductData>;

const categoryLabels: Record<string, string> = {
  electronics: 'Eletrônicos',
  furniture: 'Móveis',
  clothing: 'Vestuário',
  food: 'Alimentos',
  books: 'Livros',
  toys: 'Brinquedos',
  sports: 'Esportes',
  beauty: 'Beleza',
  automotive: 'Automotivo',
  home_garden: 'Casa e Jardim',
  other: 'Outros'
};

const statusLabels: Record<string, string> = {
  active: 'Ativo',
  inactive: 'Inativo',
  outofstock: 'Sem estoque',
  discontinued: 'Descontinuado',
  archived: 'Arquivado'
};

/** Semântica de cor do status → StatusPill shared (F62). */
const statusVariants: Record<string, StatusPillVariant> = {
  active: 'success',
  inactive: 'neutral',
  outofstock: 'error',
  discontinued: 'warning',
  archived: 'neutral'
};

interface ProductsContentProps {
  databaseId: string;
}

const getTotalStock = (data: DatabaseProductData): number | null => {
  if (data.variations && data.variations.length > 0) {
    return data.variations.reduce((sum: number, v: ProductVariation) => sum + (v.stock || 0), 0);
  }
  return data.inventory?.stock ?? null;
};

const productCategoryLabel = (cat?: string): string =>
  (cat && categoryLabels[cat]) || cat || 'N/A';

const getMainPrice = (data?: DatabaseProductData): number => {
  if (!data) return 0;
  const opp = data.opportunities?.find((o: BusinessOpportunity) => o.type === 'sale') || data.opportunities?.[0];
  return opp?.pricing?.amount ?? 0;
};

type SortKey = 'name' | 'category' | 'stock' | 'status' | 'price';

/** Motivo da recusa item a item, em português (o servidor manda só o código). */
const rejectionLabels: Record<DatabaseDocumentTransferRejectionCode, string> = {
  not_found: 'registro não encontrado',
  wrong_type: 'a base de destino é de outro tipo',
  inactive_target: 'a base de destino está inativa',
  integration_target: 'a base de destino é de integração',
  integration_linked: 'o registro veio de integração (ERP)',
  same_database: 'o destino é a própria base atual',
  write_failed: 'falha ao gravar',
};

const rejectionLabel = (code?: DatabaseDocumentTransferRejectionCode): string =>
  (code && rejectionLabels[code]) || 'motivo não informado';

/** Motivo do "Mover" bloqueado (D1) — vira dica no computador. */
const MOVE_BLOCKED_REASON = 'Registro veio de integração (ERP) e não pode ser movido';

export interface ProductActionHandlers {
  onOpen: (id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onDuplicate: (id: string) => void;
  onCopy: (id: string) => void;
  onMove: (id: string) => void;
}

/**
 * Ações do menu ••• (compartilhadas entre card e linha).
 * No celular o menu vira folha de ações, que ignora `disabledReason` — por isso,
 * ali, o motivo do "Mover" bloqueado entra no próprio rótulo.
 */
const buildProductActions = (
  product: ProductDocument,
  handlers: ProductActionHandlers,
  isMobile: boolean,
): CardAction[] => {
  const id = product.id;
  const moveBlocked = isDocumentIntegrationLinked(product);
  return [
    { label: 'Abrir', icon: 'eye', onClick: () => handlers.onOpen(id) },
    { label: 'Editar', icon: 'edit', onClick: () => handlers.onEdit(id) },
    { label: 'Duplicar aqui', icon: 'copy', onClick: () => handlers.onDuplicate(id), dividerBefore: true },
    { label: 'Copiar para outra base', icon: 'copy', onClick: () => handlers.onCopy(id) },
    {
      label: moveBlocked && isMobile ? 'Mover para outra base (registro do ERP)' : 'Mover para outra base',
      icon: 'arrow-right',
      onClick: () => handlers.onMove(id),
      disabled: moveBlocked,
      disabledReason: moveBlocked ? MOVE_BLOCKED_REASON : undefined,
    },
    { label: 'Excluir', icon: 'trash', onClick: () => handlers.onDelete(id), variant: 'danger', dividerBefore: true },
  ];
};

export const ProductsContent: React.FC<ProductsContentProps> = ({ databaseId }) => {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const isMobile = useIsMobile();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [advancedFilters, setAdvancedFilters] = useState<ProductsFilters>({});
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'name', dir: 'asc' });
  /** Alvo do modal de copiar/mover; `null` = fechado. */
  const [transfer, setTransfer] = useState<{ mode: 'copy' | 'move'; documentIds: string[] } | null>(null);

  const deleteProduct = useDeleteDatabaseDocument();

  const { data, isLoading, isFetching, error, refetch } = useDatabaseDocuments<DatabaseProductData>({
    databaseId,
    filters: {
      type: 'products',
      search: searchTerm || undefined,
      ...(advancedFilters.category?.length && { category: advancedFilters.category }),
      ...(advancedFilters.itemStatus?.length && { itemStatus: advancedFilters.itemStatus[0] }),
      ...(advancedFilters.opportunityType?.length && { opportunityType: advancedFilters.opportunityType[0] }),
      ...(advancedFilters.minPrice && { minPrice: advancedFilters.minPrice }),
      ...(advancedFilters.maxPrice && { maxPrice: advancedFilters.maxPrice }),
      ...(advancedFilters.stockStatus && { stockStatus: advancedFilters.stockStatus }),
      ...(advancedFilters.hasVariations !== undefined && { hasVariations: advancedFilters.hasVariations }),
    },
    page: currentPage,
    limit: 12
  });

  const handleSearchChange = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handleFiltersChange = (filters: ProductsFilters) => {
    setAdvancedFilters(filters);
    setCurrentPage(1);
  };

  const goCreate = () => navigate(`/databases/${databaseId}/documents/new?type=products`);

  const items = useMemo(() => data?.items ?? [], [data]);
  const hasItems = items.length > 0;
  const activeFilterCount = Object.keys(advancedFilters).length;
  const isFiltered = !!searchTerm || activeFilterCount > 0;

  // Sort client-side na página carregada (paridade com o padrão-lista).
  const sortedItems = useMemo(() => {
    const copy = [...items];
    copy.sort((a, b) => {
      let cmp = 0;
      if (sort.key === 'name') cmp = (a.data?.name ?? '').localeCompare(b.data?.name ?? '', 'pt-BR');
      else if (sort.key === 'category') cmp = productCategoryLabel(a.data?.category).localeCompare(productCategoryLabel(b.data?.category), 'pt-BR');
      else if (sort.key === 'stock') cmp = (a.data ? getTotalStock(a.data) ?? -1 : -1) - (b.data ? getTotalStock(b.data) ?? -1 : -1);
      else if (sort.key === 'status') cmp = (a.data?.itemStatus ?? '').localeCompare(b.data?.itemStatus ?? '', 'pt-BR');
      else cmp = getMainPrice(a.data) - getMainPrice(b.data);
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [items, sort]);

  const copyDocuments = useCopyDatabaseDocuments();

  // Seleção da página (hook compartilhado). A chave de reinício inclui o MODO DE
  // VISÃO: sem ele, trocar Lista↔Cards mantém a seleção viva (D4). Também limpa ao
  // paginar, buscar e filtrar — o defeito que existia aqui.
  const pageIds = useMemo(() => items.map((p) => p.id), [items]);
  const selectionResetKey = [
    databaseId,
    viewMode,
    currentPage,
    searchTerm,
    JSON.stringify(advancedFilters),
  ].join('|');
  const selection = usePageSelection({ pageIds, resetKey: selectionResetKey });

  // id → nome dos 12 da página: o modal e os avisos escrevem o nome do registro
  // recusado sem nenhuma busca extra.
  const titleById = useMemo<Record<string, string>>(
    () => Object.fromEntries(items.map((p) => [p.id, p.data?.name || 'Sem nome'])),
    [items],
  );

  const handleSort = (key: SortKey) =>
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

  const openProduct = (id: string) => navigate(`/databases/${databaseId}/documents/${id}`);
  const editProduct = (id: string) => navigate(`/databases/${databaseId}/documents/${id}/edit`);

  const handleDelete = async (id: string) => {
    if (!(await confirm({ message: 'Excluir este produto?', title: 'Confirmar exclusão', confirmButtonText: 'Excluir', variant: 'danger' }))) return;
    deleteProduct.mutate(id);
    selection.deselect(id);
  };

  const bulkDelete = async () => {
    if (!(await confirm({ message: `Excluir ${selection.selectedCount} produto(s) selecionado(s)?`, title: 'Confirmar exclusão', confirmButtonText: 'Excluir', variant: 'danger' }))) return;
    Array.from(selection.selectedIds).forEach((id) => deleteProduct.mutate(id));
    selection.clear();
  };

  /** Um aviso para o que deu certo e outro, com nome e motivo, para o que foi recusado. */
  const reportDuplicateResult = (result: DatabaseDocumentTransferResult) => {
    if (result.summary.succeeded > 0) {
      toast.success(`${result.summary.succeeded} produto(s) duplicado(s)`);
    }
    const rejected = result.results.filter((r) => r.status === 'rejected');
    if (rejected.length > 0) {
      const details = rejected
        .map((r) => `${titleById[r.documentId] ?? r.documentId}: ${rejectionLabel(r.reasonCode)}`)
        .join('; ');
      toast.error(`${rejected.length} não ${rejected.length === 1 ? 'foi duplicado' : 'foram duplicados'} — ${details}`);
    }
  };

  /** "Duplicar aqui" = copiar com destino igual à base atual — sem modal (D2). */
  const runDuplicate = async (documentIds: string[]) => {
    if (documentIds.length === 0) return;
    try {
      const result: DatabaseDocumentTransferResult = await copyDocuments.mutateAsync({
        documentIds,
        targetDatabaseId: databaseId,
        sourceDatabaseId: databaseId,
      });
      reportDuplicateResult(result);
      selection.clear();
    } catch {
      toast.error('Não foi possível duplicar');
    }
  };

  const duplicateProduct = (id: string) => { void runDuplicate([id]); };

  const bulkDuplicate = async () => {
    const documentIds = Array.from(selection.selectedIds);
    if (documentIds.length === 0) return;
    // D9: a confirmação diz quantos registros nascem, porque cada um dispara as
    // automações de "documento criado" na base de destino.
    if (!(await confirm({
      message: `Duplicar ${documentIds.length} produto(s) nesta mesma base? Serão criados ${documentIds.length} registro(s) novo(s), e as automações de "documento criado" disparam para cada um.`,
      title: 'Duplicar aqui',
      confirmButtonText: 'Duplicar',
      variant: 'default',
    }))) return;
    await runDuplicate(documentIds);
  };

  const openTransfer = (mode: 'copy' | 'move', documentIds: string[]) => {
    if (documentIds.length === 0) return;
    setTransfer({ mode, documentIds });
  };

  const copyProduct = (id: string) => openTransfer('copy', [id]);
  const moveProduct = (id: string) => openTransfer('move', [id]);

  /** Quantos dos selecionados o backend aceitaria mover (D1). Zero = botão em massa desligado. */
  const selectedMovableCount = items.filter(
    (p) => selection.isSelected(p.id) && !isDocumentIntegrationLinked(p),
  ).length;

  const rowHandlers = {
    onOpen: openProduct,
    onEdit: editProduct,
    onDelete: handleDelete,
    onDuplicate: duplicateProduct,
    onCopy: copyProduct,
    onMove: moveProduct,
  };

  if (error) {
    return (
      <div className="h-full flex items-center justify-center p-6">
        <div
          className="rounded-lg p-4 max-w-md text-center"
          style={{ backgroundColor: 'var(--theme-error-subtle)', border: '1px solid var(--theme-error-color)' }}
        >
          <p style={{ color: 'var(--theme-error-fg, var(--theme-error-color))' }}>
            Erro ao carregar produtos: {error.message}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* ── Barra de ações do conteúdo (3 zonas) ── */}
      <PageToolbar
        left={<>
          {/* Esquerda: contador de resultados */}
          <Icon name="package" className="w-4 h-4 flex-none hidden sm:inline" style={{ color: 'var(--theme-muted-text)' }} />
          {data?.total != null && (
            <span
              className="text-xs font-semibold rounded-full px-2 py-0.5 tabular-nums flex-none"
              style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' }}
            >
              {data.total}
            </span>
          )}
        </>}
        center={
          <ToolbarSearch
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Buscar produtos…"
            data-testid="products-search"
          />
        }
        right={<>
          {/* Direita: refresh + view toggle + Cadastrar */}
          {/* Refresh (F07: casca 36×36 bordada + ícone 16 + hover ghost) */}
          <button
            onClick={() => refetch()}
            className="flex items-center justify-center h-9 w-9 rounded-lg transition-colors flex-none"
            style={{ border: '1px solid var(--theme-input-border)', backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-muted-text)' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-card-background)'; }}
            title="Atualizar dados"
            disabled={isFetching}
            data-testid="products-refresh"
          >
            <Icon name="refresh" className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>

          {/* View toggle (F09-11: shared; F10: Lista primeiro TAMBÉM em Produtos) */}
          <ViewToggle
            modes={[
              { id: 'list', icon: 'list', label: 'Lista' },
              { id: 'grid', icon: 'grid', label: 'Cards' },
            ]}
            value={viewMode}
            onChange={setViewMode}
            data-testid="products-view"
          />

          {/* Cadastrar (único primário — Button shared, F01-03) */}
          <Button
            variant="primary"
            icon="plus"
            collapsibleLabel
            onClick={goCreate}
            data-testid="products-create"
          >
            Cadastrar
          </Button>
        </>}
      />

      {/* ── Filterbar de chips (filtros avançados) ── */}
      <ProductsFilterChipsBar
        filters={advancedFilters}
        onFiltersChange={handleFiltersChange}
      />

      {/* ── Bulk bar — D4: só na visão Lista (aqui o id é `'list'`, não `'table'`)
           e só no computador. O corte é por CSS (`hidden md:flex` = o MESMO 768px
           da tabela), não pelo `useIsMobile`, que corta em 1023px e esconderia a
           barra numa largura em que a tabela ainda aparece. ── */}
      {viewMode === 'list' && selection.selectedCount > 0 && (
        <div className="flex-none hidden md:flex items-center gap-2.5 px-4 py-2" style={{ backgroundColor: 'var(--theme-primary-background)', borderBottom: '1px solid var(--theme-header-border)' }}>
          <span className="font-bold text-sm" style={{ color: 'var(--theme-primary)' }}>{selection.selectedCount} selecionado(s)</span>
          <span className="flex-1" />
          <button onClick={() => { void bulkDuplicate(); }} className="inline-flex items-center gap-1.5 h-[30px] px-2.5 rounded-lg text-sm font-semibold" style={{ backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-primary)' }} data-testid="products-bulk-duplicate">
            <Icon name="copy" className="w-3.5 h-3.5" /> Duplicar aqui
          </button>
          <button onClick={() => openTransfer('copy', Array.from(selection.selectedIds))} className="inline-flex items-center gap-1.5 h-[30px] px-2.5 rounded-lg text-sm font-semibold" style={{ backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-primary)' }} data-testid="products-bulk-copy">
            <Icon name="copy" className="w-3.5 h-3.5" /> Copiar para outra base
          </button>
          <button
            onClick={() => openTransfer('move', Array.from(selection.selectedIds))}
            disabled={selectedMovableCount === 0}
            className="inline-flex items-center gap-1.5 h-[30px] px-2.5 rounded-lg text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-primary)' }}
            data-testid="products-bulk-move"
          >
            <Icon name="arrow-right" className="w-3.5 h-3.5" />
            {selectedMovableCount === 0 ? 'Mover (registros do ERP)' : 'Mover para outra base'}
          </button>
          <button onClick={() => { void bulkDelete(); }} className="inline-flex items-center gap-1.5 h-[30px] px-2.5 rounded-lg text-sm font-semibold" style={{ backgroundColor: 'var(--theme-error-subtle)', color: 'var(--theme-error-fg)' }} data-testid="products-bulk-delete">
            <Icon name="trash" className="w-3.5 h-3.5" /> Excluir
          </button>
          <button onClick={selection.clear} className="text-sm font-semibold" style={{ color: 'var(--theme-primary)' }} data-testid="products-bulk-cancel">Cancelar</button>
        </div>
      )}

      {/* ── Conteúdo ── */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-6">
        {isLoading && <ListPageLoading message="Carregando produtos..." />}

        {!isLoading && !hasItems && (
          <ListPageEmpty
            icon="package"
            message="Nenhum produto encontrado"
            description={isFiltered ? 'Tente ajustar a busca ou os filtros.' : 'Não há produtos cadastrados neste banco de dados'}
            actionLabel={isFiltered ? undefined : 'Cadastrar'}
            onAction={isFiltered ? undefined : goCreate}
          />
        )}

        {/* Grid (Cards) — kebab ••• (F39: paridade com Documentos) */}
        {!isLoading && hasItems && viewMode === 'grid' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-5">
            {items.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onClick={() => navigate(`/databases/${databaseId}/documents/${product.id}`)}
                actions={buildProductActions(product, rowHandlers, isMobile)}
              />
            ))}
          </div>
        )}

        {/* Lista (tabela no desktop, cards no mobile) */}
        {!isLoading && hasItems && viewMode === 'list' && (
          <>
            {/* Mobile: cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:hidden">
              {items.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  onClick={() => navigate(`/databases/${databaseId}/documents/${product.id}`)}
                  actions={buildProductActions(product, rowHandlers, isMobile)}
                />
              ))}
            </div>

            {/* Desktop: tabela */}
            <div
              className="hidden md:block rounded-xl overflow-hidden"
              style={{
                backgroundColor: 'var(--theme-card-background)',
                border: '1px solid var(--theme-card-border)'
              }}
            >
              <table className="w-full border-collapse">
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--theme-card-border)' }}>
                    <SelectAllHeadCell
                      checked={selection.allOnPageSelected}
                      indeterminate={selection.someOnPageSelected && !selection.allOnPageSelected}
                      onToggle={selection.toggleAllOnPage}
                      data-testid="products-select-all"
                    />
                    <SortableHeadCell label="Produto" sortKey="name" activeKey={sort.key} dir={sort.dir} onSort={handleSort} />
                    <SortableHeadCell label="Categoria" sortKey="category" activeKey={sort.key} dir={sort.dir} onSort={handleSort} />
                    <SortableHeadCell label="Estoque" sortKey="stock" activeKey={sort.key} dir={sort.dir} onSort={handleSort} />
                    <SortableHeadCell label="Status" sortKey="status" activeKey={sort.key} dir={sort.dir} onSort={handleSort} />
                    <SortableHeadCell label="Preço" sortKey="price" activeKey={sort.key} dir={sort.dir} onSort={handleSort} />
                    <PlainHeadCell className="w-14" />
                  </tr>
                </thead>
                <tbody>
                  {sortedItems.map((product) => (
                    <ProductListItem
                      key={product.id}
                      product={product}
                      selected={selection.isSelected(product.id)}
                      onToggleSelect={selection.toggle}
                      isMobile={isMobile}
                      {...rowHandlers}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Paginação — "Mostrando X–Y de N" universal (F48) */}
        {!isLoading && data && (
          <ListPagePagination
            currentPage={data.page}
            totalPages={data.totalPages}
            onPageChange={setCurrentPage}
            totalItems={data.total}
            pageSize={12}
            itemLabel="produtos"
          />
        )}
      </div>

      {/* Copiar / Mover para outra base (D2). Montado SÓ quando aberto: os hooks de
          dentro do modal rodam antes de qualquer `if (!isOpen) return null`. */}
      {transfer && (
        <TransferDocumentsDialog
          isOpen
          mode={transfer.mode}
          sourceDatabaseId={databaseId}
          databaseType={DatabaseType.PRODUCTS}
          documentIds={transfer.documentIds}
          titleById={titleById}
          onClose={() => setTransfer(null)}
          onTransferred={() => selection.clear()}
        />
      )}
    </div>
  );
};

/**
 * Item de lista de produto (Table View)
 */
const ProductListItem: React.FC<ProductActionHandlers & {
  product: ProductDocument;
  selected: boolean;
  onToggleSelect: (id: string) => void;
  /** Celular = folha de ações (sem dica): o motivo do "Mover" bloqueado vai no rótulo. */
  isMobile: boolean;
}> = ({ product, selected, onToggleSelect, isMobile, onOpen, onEdit, onDelete, onDuplicate, onCopy, onMove }) => {
  const productData = product.data;
  const totalStock = productData ? getTotalStock(productData) : null;
  const mainOpportunity = productData?.opportunities?.find((opp: BusinessOpportunity) => opp.type === 'sale') || productData?.opportunities?.[0];
  const variationsCount = productData?.variations?.length || 0;
  const actions = buildProductActions(product, { onOpen, onEdit, onDelete, onDuplicate, onCopy, onMove }, isMobile);

  return (
    <tr
      className="border-b transition-colors cursor-pointer"
      style={{ borderColor: 'var(--theme-card-border)', backgroundColor: selected ? 'var(--theme-primary-background)' : undefined }}
      onClick={() => onOpen(product.id)}
      onMouseEnter={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
      onMouseLeave={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'transparent'; }}
      data-testid={`product-row-${product.id}`}
    >
      {/* Quadradinho — célula compartilhada: o clique morre no <td>, senão marcar abre o registro. */}
      <SelectRowCell
        checked={selected}
        onToggle={() => onToggleSelect(product.id)}
        ariaLabel={selected ? 'Desmarcar' : 'Selecionar'}
        data-testid={`product-row-check-${product.id}`}
      />
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          {productData?.media?.photos?.[0] ? (
            <img
              src={productData.media.photos[0]}
              alt={productData?.name || 'Produto'}
              className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
            />
          ) : (
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: 'var(--theme-muted-background, #f1f5f9)' }}
            >
              <Icon name="package" className="w-5 h-5" style={{ color: '#64748b' }} />
            </div>
          )}
          <div>
            <div className="font-medium" style={{ color: 'var(--theme-card-text)' }}>
              {productData?.name || 'Sem nome'}
            </div>
            {productData?.sku && (
              <div className="text-xs mt-0.5" style={{ color: 'var(--theme-muted-text)' }}>
                SKU: {productData.sku}
              </div>
            )}
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5">
          <Icon name="tag" className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--theme-muted-text)' }} />
          <span className="text-sm" style={{ color: 'var(--theme-card-text)' }}>
            {categoryLabels[productData?.category || ''] || productData?.category || 'N/A'}
          </span>
        </div>
        {productData?.brand && (
          <div className="text-xs mt-0.5" style={{ color: 'var(--theme-muted-text)' }}>
            {productData.brand}
          </div>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="text-sm" style={{ color: 'var(--theme-card-text)' }}>
          {totalStock !== null ? (
            <span style={{
              color: totalStock === 0 ? 'var(--theme-error-color)' : 'var(--theme-card-text)'
            }}>
              {totalStock === 0 ? 'Sem estoque' : `${totalStock} un.`}
            </span>
          ) : (
            <span style={{ color: 'var(--theme-muted-text)' }}>N/A</span>
          )}
        </div>
        {variationsCount > 0 && (
          <div className="text-xs mt-0.5" style={{ color: 'var(--theme-muted-text)' }}>
            {variationsCount} {variationsCount === 1 ? 'variação' : 'variações'}
          </div>
        )}
      </td>
      <td className="px-4 py-3">
        {productData?.itemStatus && (
          <StatusPill
            variant={statusVariants[productData.itemStatus] ?? 'neutral'}
            label={statusLabels[productData.itemStatus] || productData.itemStatus}
            data-testid={`product-row-status-${product.id}`}
          />
        )}
      </td>
      <td className="px-4 py-3">
        {mainOpportunity?.pricing?.amount ? (
          <span
            className="inline-flex px-2 py-0.5 rounded text-xs font-semibold"
            style={{
              backgroundColor: 'var(--theme-success-background)',
              color: 'var(--theme-success-color)'
            }}
          >
            {formatPrice(mainOpportunity.pricing.amount)}
          </span>
        ) : (
          <span className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
            Sem preço
          </span>
        )}
      </td>

      {/* Ações */}
      <td className="px-4 py-3 text-right w-14" onClick={(e) => e.stopPropagation()}>
        <CardActionsMenu actions={actions} size="sm" data-testid={`product-row-actions-${product.id}`} />
      </td>
    </tr>
  );
};
